import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link as RouterLink, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  Alert,
  Box,
  Button,
  GlobalStyles,
  Link,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import LoginIcon from "@mui/icons-material/Login";
import { login } from "../api/auth";
import AuthPageShell from "../components/auth/AuthPageShell";
import PasswordField from "../components/auth/PasswordField";
import SocialSignInButtons from "../components/auth/SocialSignInButtons";
import {
  loginSucceeded,
  selectAuthToken,
} from "../features/auth/authSlice";
import {
  getAuthEnvironmentSwitchUrl,
  openAuthEnvironmentSwitchUrl,
} from "../utils/environment";

const isCredentialEditingKey = (event) => {
  if (event.ctrlKey || event.metaKey || event.altKey) {
    return false;
  }

  return (
    event.key.length === 1 ||
    event.key === "Backspace" ||
    event.key === "Delete"
  );
};

const safeReturnTo = (value) => {
  const trimmed = String(value || "").trim();

  return trimmed.startsWith("/") && !trimmed.startsWith("//") ? trimmed : "/dashboard";
};

const inviteReturnTo = (searchParams) => {
  const explicitReturnTo = searchParams.get("returnTo");
  if (explicitReturnTo) return safeReturnTo(explicitReturnTo);

  const nightId = Number(searchParams.get("nightId"));
  if (Number.isInteger(nightId) && nightId > 0) {
    return `/invite/gathering/night/${nightId}`;
  }

  return "/dashboard";
};

function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const token = useSelector(selectAuthToken);

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [unverifiedIdentifier, setUnverifiedIdentifier] = useState("");
  const usernameInputRef = useRef(null);
  const passwordInputRef = useRef(null);
  const autofillSubmitKeyRef = useRef("");
  const environmentTapCountRef = useRef(0);
  const environmentTapTimerRef = useRef();
  const userEditedCredentialsRef = useRef(false);
  const accountDeleted = location.state?.accountDeleted === true;
  const oauthError = location.state?.oauthError;
  const returnTo = inviteReturnTo(searchParams);
  const trimmedUsername = username.trim();
  const canAutoSubmitFilledCredentials =
    !userEditedCredentialsRef.current && Boolean(trimmedUsername && password);

  useEffect(() => {
    if (token) {
      navigate(returnTo, { replace: true });
    }
  }, [navigate, returnTo, token]);

  useEffect(
    () => () => {
      window.clearTimeout(environmentTapTimerRef.current);
    },
    []
  );

  const syncAutofilledCredentials = useCallback(() => {
    const nextUsername = usernameInputRef.current?.value || "";
    const nextPassword = passwordInputRef.current?.value || "";
    const usernameChanged = nextUsername !== username;
    const passwordChanged = nextPassword !== password;

    if (usernameChanged) {
      setUsername(nextUsername);
    }

    if (passwordChanged) {
      setPassword(nextPassword);
    }

  }, [password, username]);

  useEffect(() => {
    let active = true;
    let timeoutId;
    let attempts = 0;

    const pollForAutofill = () => {
      if (!active) {
        return;
      }

      syncAutofilledCredentials();
      attempts += 1;

      if (attempts < 100) {
        timeoutId = window.setTimeout(pollForAutofill, 100);
      }
    };

    const restartPolling = () => {
      attempts = 0;
      window.clearTimeout(timeoutId);
      pollForAutofill();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        restartPolling();
      }
    };

    pollForAutofill();
    window.addEventListener("focus", restartPolling);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      active = false;
      window.clearTimeout(timeoutId);
      window.removeEventListener("focus", restartPolling);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [syncAutofilledCredentials]);

  const handleAutofillAnimation = () => {
    window.setTimeout(syncAutofilledCredentials, 0);
  };

  const handleLogoTap = useCallback(() => {
    if (!getAuthEnvironmentSwitchUrl()) return;

    window.clearTimeout(environmentTapTimerRef.current);
    environmentTapCountRef.current += 1;

    if (environmentTapCountRef.current >= 3) {
      environmentTapCountRef.current = 0;
      openAuthEnvironmentSwitchUrl();
      return;
    }

    environmentTapTimerRef.current = window.setTimeout(() => {
      environmentTapCountRef.current = 0;
    }, 900);
  }, []);

  const markCredentialsAsUserEdited = (event) => {
    if (event?.type === "keydown" && !isCredentialEditingKey(event)) {
      return;
    }

    userEditedCredentialsRef.current = true;
    autofillSubmitKeyRef.current = "";
  };

  const handleUsernameChange = (e) => {
    const nextUsername = e.target.value;

    setUsername(nextUsername);
    window.setTimeout(syncAutofilledCredentials, 0);
  };

  const handlePasswordChange = (e) => {
    const nextPassword = e.target.value;

    setPassword(nextPassword);
    window.setTimeout(syncAutofilledCredentials, 0);
  };

  const submitLogin = useCallback(async () => {
    const identifier = username.trim();
    if (!identifier || !password || loading) return;

    setError("");
    setUnverifiedIdentifier("");
    setLoading(true);

    try {
      const data = await login(identifier, password);

      if (data.token) {
        dispatch(loginSucceeded(data.token));
        navigate(returnTo, { replace: true });
      }
    } catch (err) {
      if (err.response?.data?.code === "EMAIL_NOT_VERIFIED") {
        setUnverifiedIdentifier(err.response.data.email || username);
        setError(t("login.emailNotVerified"));
        return;
      }

      setError(err.response?.data?.error || t("login.genericError"));
    } finally {
      setLoading(false);
    }
  }, [dispatch, loading, navigate, password, returnTo, t, username]);

  useEffect(() => {
    if (
      !canAutoSubmitFilledCredentials ||
      loading ||
      error ||
      unverifiedIdentifier
    ) {
      return undefined;
    }

    const submitKey = `${trimmedUsername}\n${password}`;
    if (autofillSubmitKeyRef.current === submitKey) return undefined;

    const timeoutId = window.setTimeout(() => {
      const domUsername = usernameInputRef.current?.value || "";
      const domPassword = passwordInputRef.current?.value || "";
      const stableSubmitKey = `${domUsername.trim()}\n${domPassword}`;

      if (
        userEditedCredentialsRef.current ||
        stableSubmitKey !== submitKey ||
        autofillSubmitKeyRef.current === submitKey
      ) {
        return;
      }

      autofillSubmitKeyRef.current = submitKey;
      submitLogin();
    }, 500);

    return () => window.clearTimeout(timeoutId);
  }, [
    canAutoSubmitFilledCredentials,
    error,
    loading,
    password,
    submitLogin,
    trimmedUsername,
    unverifiedIdentifier,
  ]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    submitLogin();
  };

  if (token) return null;

  return (
    <AuthPageShell
      title={t("login.title")}
      subtitle={t("login.subtitle")}
      onLogoClick={handleLogoTap}
      showTitle={false}
    >
      <GlobalStyles
        styles={{
          "@keyframes tavernAutofill": {
            from: { opacity: 1 },
            to: { opacity: 1 },
          },
          "input:-webkit-autofill": {
            animationDuration: "0.01s",
            animationName: "tavernAutofill",
          },
        }}
      />
      <Stack component="form" spacing={2} onSubmit={handleSubmit}>
          {accountDeleted && (
            <Alert severity="success">{t("profile.delete.success")}</Alert>
          )}
          {oauthError && <Alert severity="error">{t("auth.social.error")}</Alert>}

          <TextField
            id="username"
            label={t("login.identifier")}
            value={username}
            onChange={handleUsernameChange}
            onKeyDown={markCredentialsAsUserEdited}
            inputRef={usernameInputRef}
            fullWidth
            autoComplete="username"
            inputProps={{ onAnimationStart: handleAutofillAnimation }}
          />

          <PasswordField
            id="password"
            label={t("login.password")}
            value={password}
            onChange={handlePasswordChange}
            onKeyDown={markCredentialsAsUserEdited}
            inputRef={passwordInputRef}
            fullWidth
            autoComplete="current-password"
            inputProps={{ onAnimationStart: handleAutofillAnimation }}
          />

          <Link
            component={RouterLink}
            to="/forgot-password"
            underline="hover"
            sx={{ alignSelf: "flex-end", fontWeight: 700 }}
          >
            {t("auth.forgotPassword")}
          </Link>

          {error && <Alert severity="error">{error}</Alert>}
          {unverifiedIdentifier && (
            <Button
              variant="outlined"
              onClick={() =>
                navigate(
                  `/resend-verification?identifier=${encodeURIComponent(
                    unverifiedIdentifier
                  )}`
                )
              }
            >
              {t("login.resendVerification")}
            </Button>
          )}

          <Button
            type="submit"
            variant="contained"
            startIcon={<LoginIcon />}
            disabled={loading || !trimmedUsername || !password}
            fullWidth
          >
            {loading ? t("login.signingIn") : t("login.signIn")}
          </Button>

          <SocialSignInButtons returnTo={returnTo} />

          <Box
            sx={{
              mt: 0.5,
              pt: 2,
              borderTop: "1px solid",
              borderColor: "divider",
              textAlign: "center",
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {t("auth.entry.createPrompt")}
            </Typography>
            <Button
              component={RouterLink}
              to={`/register?returnTo=${encodeURIComponent(returnTo)}`}
              variant="outlined"
              color="secondary"
              fullWidth
              sx={{ mt: 1 }}
            >
              {t("auth.entry.createAction")}
            </Button>
          </Box>
      </Stack>
    </AuthPageShell>
  );
}

export default LoginPage;
